import React, { useState, useMemo, useEffect } from 'react';
import useGameBase from '../../hooks/useGameBase';

function Cell({ piece, onClick, highlight, selected, capture, pulse }){
  const img = piece ? `/icons/draughts/${piece}.svg` : null; // draughts icons
  return (
    <div className={`relative flex h-full w-full items-center justify-center ${piece ? 'cursor-pointer hover:bg-gray-500 dark:hover:bg-gray-500' : ''}`} onClick={onClick}>
  {piece ? <img src={img} alt={piece} className={`h-[86%] w-[86%] select-none pointer-events-none ${pulse ? 'draughts-strong-pulse' : ''}`} /> : null}
      
      {highlight && !piece ? (
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="w-3 h-3 bg-blue-400 rounded-full" />
        </div>
      ) : null}
      {highlight && capture ? (
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
          <div className="w-5 h-5 rounded-full border-2 border-blue-400" />
        </div>
      ) : null}
      {selected ? <div className="absolute  rounded-sm pointer-events-none" /> : null}
    </div>
  );
}

export default function DraughtsGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }){
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const idCandidates = useMemo(()=>[user?.socketId, user?.userId, user?.id].filter(Boolean), [user]);
  const localPid = idCandidates[0] || null;
  const board = gameState.board || Array.from({length:8}, ()=>Array(8).fill(null));
  const [selected, setSelected] = useState(null);
  const [highlights, setHighlights] = useState({}); // map 'r,c' => true

  // inject a stronger pulse animation into the document once so pulsing is more obvious
  useEffect(()=>{
    if(typeof document === 'undefined') return;
    if(document.getElementById('draughts-strong-pulse-styles')) return;
    const s = document.createElement('style');
    s.id = 'draughts-strong-pulse-styles';
  s.textContent = `@keyframes draughtsStrongPulse { 0% { transform: scale(1); } 50% { transform: scale(1.38);  } 100% { transform: scale(1); } } .draughts-strong-pulse { animation: draughtsStrongPulse 800ms ease-in-out infinite; border-radius: 9999px; }`;

    document.head.appendChild(s);
  }, []);

  // debug: log when component mounts so we can detect whether it's rendered
  useEffect(()=>{
    if(typeof window !== 'undefined' && window.console){
      console.log('DraughtsGame mounted');
    }
  }, []);

  // derive stable strings for deps to avoid complex expressions in dependency array
  const legalMovesJson = gameState ? JSON.stringify(gameState.legalMoves || {}) : '{}';
  const activeCaptureJson = gameState ? JSON.stringify(gameState.activeCapture || null) : 'null';

  // derive a detailed destination map: { 'r,c': { capture: bool, sources:[[fr,fc],...] } }
  const destMeta = useMemo(()=>{
    const out = {};
    let lm = {};
    try{ lm = JSON.parse(legalMovesJson) || {}; }catch(e){ lm = {}; }
    Object.keys(lm).forEach(k=>{
      const [fr,fc] = k.split(',').map(x=>parseInt(x,10));
      (lm[k]||[]).forEach(([tr,tc])=>{
        const key = `${tr},${tc}`;
        const isCapture = Math.abs(tr - fr) === 2;
        if(!out[key]) out[key] = { capture: isCapture, sources: [] };
        out[key].sources.push([fr,fc]);
        // if any source indicates capture, mark capture true
        out[key].capture = out[key].capture || isCapture;
      });
    });
    return out;
  }, [legalMovesJson, board]);

  // compute which source squares have a capture available (so we can pulse them)
  const sourceCaptures = useMemo(()=>{
    const out = {};
    let lm = {};
    try{ lm = JSON.parse(legalMovesJson) || {}; }catch(e){ lm = {}; }
    Object.keys(lm).forEach(k=>{
      const fr = parseInt(k.split(',')[0], 10);
      const arr = lm[k]||[];
      const hasCap = arr.some(([tr,tc])=> Math.abs(tr - fr) === 2);
      if(hasCap) out[k] = true;
    });
    return out;
  }, [legalMovesJson]);

  const localCaptures = useMemo(()=>{
    const localOut = {};
    for(let r=0;r<8;r++) for(let c=0;c<8;c++){
      const p = board[r][c]; if(!p) continue;
      const color = p[0];
      const dirs = p[1]==='K' ? [[-1,-1],[-1,1],[1,-1],[1,1]] : (color==='w' ? [[-1,-1],[-1,1]] : [[1,-1],[1,1]]);
      for(const [dr,dc] of dirs){
        const mr = r+dr, mc = c+dc, tr = r+2*dr, tc = c+2*dc;
        if(tr<0||tr>7||tc<0||tc>7) continue;
        const mid = board[mr] && board[mr][mc]; const dest = board[tr] && board[tr][tc];
        if(mid && mid[0]!==color && !dest) localOut[`${r},${c}`] = true;
      }
    }
    return localOut;
  }, [board]);

  // final source captures: server-provided first, otherwise local fallback
  const finalSourceCaptures = Object.keys(sourceCaptures).length ? sourceCaptures : localCaptures;

  // determine which side the local user is playing (first player = white)
  const firstPlayer = gamePlayers && gamePlayers.length>0 ? gamePlayers[0] : null;
  const amFirst = firstPlayer && localPid && (localPid === firstPlayer.socketId || localPid === firstPlayer.userId);

  // when legalMoves, activeCapture or selection changes, compute highlights only for the selected piece
  useEffect(()=>{
    let lm = {};
    try{ lm = JSON.parse(legalMovesJson) || {}; }catch(e){ lm = {}; }

    // parse activeCaptureJson and auto-select that piece if present
    try{
      const ac = JSON.parse(activeCaptureJson);
      if(ac && typeof ac.r === 'number' && typeof ac.c === 'number'){
        // auto-select active capture if it's not already selected
        const already = selected && selected[0]===ac.r && selected[1]===ac.c;
        if(!already) setSelected([ac.r, ac.c]);
      }
    }catch(e){ /* ignore parse errors */ }

    // only show destinations when a piece is selected
    if(!selected){
      setHighlights({});
      return;
    }

    const key = `${selected[0]},${selected[1]}`;
    const arr = lm[key] || [];
    const h = {};
    arr.forEach(([r,c])=> h[`${r},${c}`] = true);
    setHighlights(h);

  }, [legalMovesJson, activeCaptureJson, selected, gameState, gamePlayers]);

  // Legend component: moved out of effect so it actually mounts and renders
  function Legend({ gamePlayers = [], gameState = {} }){
    const white = gamePlayers && gamePlayers.length>0 ? gamePlayers[0] : null;
    const black = gamePlayers && gamePlayers.length>1 ? gamePlayers[1] : null;
    const scores = useMemo(()=> Object.fromEntries((gameState.players||[]).map(p=>[p.socketId, p.score||0])), [gameState.players]);
    return (
      <div className="mt-3 w-full">
        <div className="border rounded-md bg-white dark:bg-gray-800 p-2">
          <div className="text-sm font-semibold mb-1 text-gray-800 dark:text-gray-100">Players</div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/icons/draughts/wP.svg" alt="White" className="w-10 h-10" />
              <div>
                <div className="font-medium text-gray-800 dark:text-gray-100">{white ? white.username : 'White (waiting...)'}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{white && scores[white.socketId] != null ? `${scores[white.socketId]} pts` : ''}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="font-medium text-gray-800 dark:text-gray-100">{black ? black.username : 'Black (waiting...)'}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{black && scores[black.socketId] != null ? `${scores[black.socketId]} pts` : ''}</div>
              </div>
              <img src="/icons/draughts/bP.svg" alt="Black" className="w-10 h-10" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleClick = (r,c) => {
    // if clicking the currently selected square, deselect
    if(selected && selected[0]===r && selected[1]===c){
      setSelected(null);
      return;
    }

    // if clicking a highlighted destination when a piece is selected, submit move
    if(selected && highlights[`${r},${c}`]){
      const from = selected; const to = [r,c];
      submitAction && submitAction({ type: 'move', from, to }).catch(()=>{});
      // do not clear selection; server will update activeCapture/legalMoves and useEffect will adjust
      return;
    }

    // select own piece if possible
    if(!board[r][c]) return setSelected(null);
    const isWhite = board[r][c].startsWith('w');
    const firstPlayer = gamePlayers && gamePlayers.length>0 ? gamePlayers[0] : null;
    const amFirst = firstPlayer && localPid && (localPid === firstPlayer.socketId || localPid === firstPlayer.userId);
    if((amFirst && isWhite) || (!amFirst && !isWhite) || (gamePlayers.length<2)){
      // if activeCapture exists, only allow selecting that square
      if(gameState && gameState.activeCapture){
        if(gameState.activeCapture.r === r && gameState.activeCapture.c === c) setSelected([r,c]);
        else setSelected(null);
      } else {
        setSelected([r,c]);
      }
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-2">
      <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-stone-100 via-amber-50 to-orange-100 p-2 shadow-sm dark:border-amber-900/70 dark:from-gray-900 dark:via-gray-800 dark:to-stone-900 sm:p-3">
      <div className="mx-auto grid aspect-square w-full max-w-[min(92vw,44rem)] grid-cols-8 gap-0 overflow-hidden rounded-2xl border border-gray-700 shadow-inner dark:border-gray-900">
        {board.map((row,r)=> row.map((p,c)=> {
          const meta = destMeta[`${r},${c}`] || null;
          // pulse when this square is a source of a capture and belongs to local player
          const key = `${r},${c}`;
          const belongsToLocal = p && ((p.startsWith('w') && amFirst) || (p.startsWith('b') && !amFirst));
          const pulse = belongsToLocal && !!finalSourceCaptures[key];
          return (
            <div key={`${r}-${c}`} className="relative aspect-square" style={{ backgroundColor: ((r+c)%2===0)?'#af833cff':'#6b3203ff' }}>
              <Cell piece={p} onClick={()=>handleClick(r,c)} highlight={!!highlights[`${r},${c}`]} selected={selected && selected[0]===r && selected[1]===c} capture={meta && meta.capture} pulse={pulse} />
            </div>
          );
        }))}
      </div>
      </div>
      <div className="mx-auto w-full max-w-[min(92vw,44rem)]">
        <Legend gamePlayers={gamePlayers} gameState={gameState} />
      </div>
    </div>
  );
}

DraughtsGame.Guide = function DraughtsGuide(){
  return (
    <div>
      <h3 className="font-bold mb-2">Draughts</h3>
      <p>English draughts on 8x8. Pieces move diagonally on dark squares and capture by jumping. Kings move both directions.</p>
      <p>Players take turns moving their pieces. If a piece reaches the opponent's back row, it is promoted to a king.</p>
      <p>The game ends when one player captures all of the opponent's pieces or blocks them from moving.</p>
      <p>Players can also choose to resign at any time.</p>
      <iframe width="560" height="315" src="https://www.youtube.com/embed/MOW9k_C4vFU?si=CnkEb2b4mfCx4QMA" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
    </div>
  );
};
